import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  MessageSquare, Send, X, Loader2, Check, AlertCircle, 
  ChevronUp, ChevronDown, Sparkles, Zap, Mail, Phone, 
  Calendar, FileText, User, RefreshCw, Edit3, Trash2,
  Minimize2, Maximize2
} from 'lucide-react';
import api from '../services/api';

const AgentIAChat = ({ dossierId: propDossierId = null, dossierInfo = null, onActionExecuted = null, isFloating = false }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(!isFloating);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingActions, setPendingActions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [currentDossier, setCurrentDossier] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [editedEmail, setEditedEmail] = useState({}); // Pour stocker les modifications d'email
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Détecter le dossierId depuis l'URL ou les props
  const getDossierIdFromUrl = () => {
    const match = location.pathname.match(/\/dossiers\/([^\/]+)/);
    return match ? match[1] : null;
  };

  const dossierId = propDossierId || getDossierIdFromUrl();

  // Charger les infos du dossier si on est en mode flottant et qu'on a un ID
  useEffect(() => {
    const loadDossierInfo = async () => {
      if (isFloating && dossierId && !dossierInfo) {
        try {
          const res = await api.get(`/dossiers/${dossierId}`);
          setCurrentDossier(res.data);
        } catch (error) {
          console.error('Erreur chargement dossier:', error);
        }
      } else if (dossierInfo) {
        setCurrentDossier(dossierInfo);
      }
    };
    loadDossierInfo();
  }, [dossierId, isFloating, dossierInfo]);

  useEffect(() => {
    if (dossierId) {
      loadSuggestions();
    }
    // Message d'accueil personnalisé
    const dossier = currentDossier || dossierInfo;
    const welcomeMsg = dossierId && dossier
      ? `Bonjour ! Je suis votre assistant IA pour le dossier **${dossier.reference || dossierId}** (${dossier.identite?.prenom || ''} ${dossier.identite?.nom || ''}).\n\nJe peux :\n• **Envoyer des emails** au client\n• **Changer le statut** du dossier\n• **Ajouter des remarques**\n• **Créer des tâches**\n\nQue souhaitez-vous faire ?`
      : `Bonjour ! Je suis votre assistant IA.\n\nOuvrez un dossier pour que je puisse vous aider avec des actions comme l'envoi d'emails, le changement de statut, etc.`;
    
    setMessages([{
      id: 'welcome',
      type: 'agent',
      content: welcomeMsg,
      timestamp: new Date().toISOString()
    }]);
  }, [dossierId, currentDossier]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSuggestions = async () => {
    if (!dossierId) return;
    try {
      const res = await api.get(`/agent-ia/suggestions/${dossierId}`);
      setSuggestions(res.data || []);
    } catch (error) {
      console.error('Erreur chargement suggestions:', error);
    }
  };

  const sendMessage = async (messageText = null) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/agent-ia/chat', {
        message: text,
        dossier_id: dossierId,
        session_id: sessionId,
        context: dossierInfo ? { dossier: dossierInfo } : null
      });

      // Sauvegarder le session_id pour maintenir le contexte
      if (res.data.session_id) {
        setSessionId(res.data.session_id);
      }

      const agentMessage = {
        id: Date.now().toString() + '-agent',
        type: 'agent',
        content: res.data.message,
        actions: res.data.actions || [],
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, agentMessage]);
      
      if (res.data.actions?.length > 0) {
        setPendingActions(prev => [...prev, ...res.data.actions]);
        // Initialiser les valeurs éditées pour chaque action email
        res.data.actions.forEach(action => {
          if (action.action_type === 'send_email') {
            setEditedEmail(prev => ({
              ...prev,
              [action.id]: {
                to_email: action.params?.to_email || '',
                subject: action.params?.subject || '',
                body: action.params?.body || action.preview || ''
              }
            }));
          }
        });
      }

    } catch (error) {
      console.error('Erreur agent IA:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString() + '-error',
        type: 'error',
        content: 'Désolé, une erreur est survenue. Veuillez réessayer.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const executeAction = async (action, modifications = null) => {
    try {
      // Envoyer les modifications dans le bon format
      const payload = modifications ? { modifications } : {};
      const res = await api.post(`/agent-ia/execute-action/${action.id}`, payload);
      
      // Vérifier si l'action a réussi
      if (res.data && res.data.success === false) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'error',
          content: `❌ ${res.data.message || 'Erreur lors de l\'exécution'}`,
          timestamp: new Date().toISOString()
        }]);
        return;
      }
      
      // Mettre à jour le message de succès
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'success',
        content: `✅ ${res.data?.message || action.description || 'Action exécutée avec succès'}`,
        timestamp: new Date().toISOString()
      }]);
      
      // Retirer des actions en attente
      setPendingActions(prev => prev.filter(a => a.id !== action.id));
      
      // Callback pour rafraîchir les données
      if (onActionExecuted) {
        onActionExecuted(action);
      }
      
    } catch (error) {
      console.error('Erreur exécution action:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'error',
        content: `❌ Erreur: ${error.response?.data?.detail || error.response?.data?.message || 'Impossible d\'exécuter l\'action'}`,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const cancelAction = async (action) => {
    try {
      await api.post(`/agent-ia/cancel-action/${action.id}`);
      setPendingActions(prev => prev.filter(a => a.id !== action.id));
    } catch (error) {
      console.error('Erreur annulation:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getActionIcon = (type) => {
    const icons = {
      'change_status': <FileText className="w-4 h-4" />,
      'assign_gestionnaire': <User className="w-4 h-4" />,
      'add_remarque': <MessageSquare className="w-4 h-4" />,
      'create_task': <Calendar className="w-4 h-4" />,
      'send_email': <Mail className="w-4 h-4" />,
      'send_sms': <Phone className="w-4 h-4" />,
      'schedule_rdv': <Calendar className="w-4 h-4" />
    };
    return icons[type] || <Zap className="w-4 h-4" />;
  };

  const renderAction = (action) => {
    if (action.action_type === 'send_email') {
      const emailData = editedEmail[action.id] || {
        to_email: action.params?.to_email || '',
        subject: action.params?.subject || '',
        body: action.params?.body || action.preview || ''
      };
      
      return (
        <div className="mt-3 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 border-b flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600" />
            <span className="font-medium text-sm">Email à envoyer</span>
            <span className="text-xs text-gray-400">(modifiez si besoin)</span>
          </div>
          <div className="p-3 space-y-3">
            <div>
              <div className="text-xs text-gray-500 mb-1">À:</div>
              <input
                type="email"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                value={emailData.to_email}
                onChange={(e) => {
                  setEditedEmail(prev => ({
                    ...prev,
                    [action.id]: { ...emailData, to_email: e.target.value }
                  }));
                }}
                placeholder="email@destinataire.com"
              />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Sujet:</div>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                value={emailData.subject}
                onChange={(e) => {
                  setEditedEmail(prev => ({
                    ...prev,
                    [action.id]: { ...emailData, subject: e.target.value }
                  }));
                }}
              />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Corps:</div>
              <textarea
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[150px] focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none resize-y"
                value={emailData.body}
                onChange={(e) => {
                  setEditedEmail(prev => ({
                    ...prev,
                    [action.id]: { ...emailData, body: e.target.value }
                  }));
                }}
              />
            </div>
          </div>
          <div className="bg-gray-50 px-3 py-2 border-t flex justify-end gap-2">
            <button
              onClick={() => cancelAction(action)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-red-600"
            >
              Annuler
            </button>
            <button
              onClick={() => executeAction(action, emailData)}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
            >
              <Send className="w-3 h-3" />
              Envoyer
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-2 flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
        <div className="flex-1 flex items-center gap-2">
          {getActionIcon(action.action_type)}
          <span className="text-sm">{action.description || action.action_type}</span>
        </div>
        <button
          onClick={() => cancelAction(action)}
          className="p-1 text-gray-400 hover:text-red-500"
        >
          <X className="w-4 h-4" />
        </button>
        <button
          onClick={() => executeAction(action)}
          className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
        >
          <Check className="w-3 h-3" />
          Confirmer
        </button>
      </div>
    );
  };

  // Version flottante (bouton + modal)
  if (isFloating) {
    return (
      <>
        {/* Bouton flottant */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all z-50 ${
            isOpen ? 'bg-gray-700 rotate-0' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-110'
          }`}
        >
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Sparkles className="w-6 h-6 text-white" />
          )}
          {pendingActions.length > 0 && !isOpen && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {pendingActions.length}
            </span>
          )}
        </button>

        {/* Modal de chat */}
        {isOpen && (
          <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-white" />
                <span className="font-semibold text-white">Assistant IA</span>
              </div>
              <button onClick={() => setIsMinimized(!isMinimized)} className="text-white/80 hover:text-white">
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                          msg.type === 'user'
                            ? 'bg-blue-600 text-white rounded-br-md'
                            : msg.type === 'error'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : msg.type === 'success'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-gray-100 text-gray-800 rounded-bl-md'
                        }`}
                      >
                        <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                        {msg.actions?.map(action => (
                          <div key={action.id}>{renderAction(action)}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-2xl px-4 py-3 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        <span className="text-sm text-gray-500">Réflexion en cours...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Suggestions */}
                {suggestions.length > 0 && messages.length <= 2 && (
                  <div className="px-4 pb-2 flex flex-wrap gap-1">
                    {suggestions.slice(0, 3).map((sug, idx) => (
                      <button
                        key={idx}
                        onClick={() => sendMessage(sug.prompt)}
                        className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100"
                      >
                        {sug.text}
                      </button>
                    ))}
                  </div>
                )}

                {/* Input */}
                <div className="p-3 border-t">
                  <div className="flex gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Tapez votre demande..."
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                    />
                    <button
                      onClick={() => sendMessage()}
                      disabled={!input.trim() || loading}
                      className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </>
    );
  }

  // Version intégrée (dans la page dossier)
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-white" />
          <span className="font-semibold text-white">Assistant IA</span>
          {dossierId && (
            <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">
              Dossier actif
            </span>
          )}
        </div>
        {pendingActions.length > 0 && (
          <span className="bg-amber-400 text-amber-900 text-xs px-2 py-0.5 rounded-full font-medium">
            {pendingActions.length} action(s) en attente
          </span>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px]">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                msg.type === 'user'
                  ? 'bg-blue-600 text-white rounded-br-md'
                  : msg.type === 'error'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : msg.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-gray-100 text-gray-800 rounded-bl-md'
              }`}
            >
              <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
              {msg.actions?.map(action => (
                <div key={action.id}>
                  {renderAction(action)}
                </div>
              ))}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl px-4 py-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              <span className="text-sm text-gray-500">Réflexion en cours...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && messages.length <= 2 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          <span className="text-xs text-gray-500 w-full mb-1">Suggestions :</span>
          {suggestions.map((sug, idx) => (
            <button
              key={idx}
              onClick={() => sendMessage(sug.prompt)}
              className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center gap-1"
            >
              {getActionIcon(sug.type)}
              {sug.text}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t bg-gray-50">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ex: Change le statut en 'En cours', Envoie un email de relance au client..."
            className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={2}
            disabled={loading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="px-4 bg-blue-600 text-white rounded-xl flex items-center justify-center hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentIAChat;
